import AuthenticationActions from "./AuthenticationActions";
import {
  canManageAuthentication,
  authenticationLabels,
} from "./authenticationManagement";
import React, { useEffect, useState } from "react";
import { useAuth } from "../../AuthContext";
import api from "../../api";
import EnrollmentRecord from "./EnrollmentRecord";
import { messageOf } from "./SignatureApi";
import "./signatures.css";
const statusLabels = {
  DRAFT: "Em preenchimento",
  PENDING: "Em análise",
  APPROVED: "Aprovada",
  REJECTED: "Rejeitada",
  CORRECTION: "Correção solicitada",
  REVOKED: "Desabilitada",
  EXPIRED: "Expirada",
};
const checks = {
  MATCH: "Confere",
  MISMATCH: "Divergente",
  NOT_REGISTERED: "Não exigido",
  UNREADABLE: "Não lido",
  PROFILE_MISSING: "Cadastro incompleto",
  READING_CONFLICT: "Leitura inconsistente",
};
const date = (value) =>
  value ? String(value).slice(0, 10).split("-").reverse().join("/") : "—";
export default function UserSignatureIdentification({ userId, onChanged }) {
  const { user } = useAuth();
  const allowed = canManageAuthentication(user);
  const [view, setView] = useState({
    key: "",
    record: null,
    busy: true,
    error: "",
  });
  const [revision, setRevision] = useState(0);
  const [recordId, setRecordId] = useState(null),
    [history, setHistory] = useState(null),
    [historyError, setHistoryError] = useState("");
  const loadHistory = async (page = 1) => {
    setHistoryError("");
    try {
      const { data } = await api.get(
        `/signatures/enrollment/users/${userId}/history`,
        { params: { page } },
      );
      setHistory(data);
    } catch (e) {
      setHistoryError(messageOf(e));
    }
  };
  const key = `${user?.id}:${userId}:${recordId || "current"}`;
  useEffect(() => {
    if (!allowed || !userId) return;
    let alive = true;
    setView({ key, record: null, busy: true, error: "" });
    api
      .get(
        `/signatures/enrollment/users/${userId}${recordId ? `?record=${recordId}` : ""}`,
      )
      .then(({ data }) => {
        if (data && Number(data.userId) !== Number(userId))
          throw new Error(
            "A identificação recebida não corresponde ao usuário selecionado.",
          );
        if (alive) setView({ key, record: data, busy: false, error: "" });
      })
      .catch((e) => {
        if (alive)
          setView({ key, record: null, busy: false, error: messageOf(e) });
      });
    return () => {
      alive = false;
    };
  }, [allowed, userId, key, revision, recordId]);
  const { record, busy, error } =
    view.key === key ? view : { record: null, busy: true, error: "" };
  if (!canManageAuthentication(user) || !userId) return null;
  return (
    <section
      className="signature-panel"
      aria-label="Identificação do usuário para assinaturas"
    >
      <div className="signature-toolbar">
        <strong>
          {record
            ? authenticationLabels[record.status] ||
              statusLabels[record.status] ||
              record.status
            : "Identificação"}
        </strong>
        <button
          type="button"
          disabled={busy}
          onClick={() => setRevision((v) => v + 1)}
        >
          Atualizar identificação
        </button>
      </div>
      {recordId && (
        <div className="authentication-request-notice">
          <strong>Você está consultando um registro anterior</strong>
          <p>As ações de gestão estão no registro atual.</p>
          <button type="button" onClick={() => setRecordId(null)}>
            Voltar ao registro atual
          </button>
        </div>
      )}
      {!recordId && (
        <details
          className="signature-card"
          onToggle={(e) => {
            if (e.currentTarget.open) loadHistory();
          }}
        >
          <summary>Todos os registros deste usuário</summary>
          {historyError && (
            <p role="alert" className="signature-error">
              {historyError}
            </p>
          )}
          {history ? (
            <>
              <p className="signature-help">
                {history.total} registro(s). Os arquivos seguem o prazo de
                retenção original.
              </p>
              {history.rows.map((item) => (
                <div className="signature-toolbar" key={item.id}>
                  <span>
                    {new Date(item.createdAt).toLocaleString("pt-BR")} ·{" "}
                    {authenticationLabels[item.status] || item.status}
                  </span>
                  <button type="button" onClick={() => setRecordId(item.id)}>
                    Consultar registro
                  </button>
                </div>
              ))}
              <div className="signature-row-actions">
                <button
                  type="button"
                  disabled={history.page <= 1}
                  onClick={() => loadHistory(history.page - 1)}
                >
                  Anteriores
                </button>
                <button
                  type="button"
                  disabled={history.page * 20 >= history.total}
                  onClick={() => loadHistory(history.page + 1)}
                >
                  Próximos registros
                </button>
              </div>
            </>
          ) : (
            <p role="status">Carregando histórico…</p>
          )}
        </details>
      )}
      {!recordId && (
        <AuthenticationActions
          key={key}
          userId={userId}
          onChanged={() => {
            setRevision((v) => v + 1);
            onChanged?.();
          }}
        />
      )}
      {record?.note && (
        <p className="signature-help">Orientação atual: {record.note}</p>
      )}
      {record?.managementHistory?.length > 0 && (
        <details className="signature-card">
          <summary>Histórico de solicitações</summary>
          {record.managementHistory
            .slice()
            .reverse()
            .map((event, i) => (
              <p key={i}>
                <strong>
                  {{
                    BLOCK: "Bloqueio",
                    RESTART: "Recomeçar do zero",
                    DOCUMENT: "Reenvio de documento",
                    SELFIE: "Nova foto",
                    DOCUMENT_REPLACED_BY_MANAGER:
                      "Documento substituído pelo responsável",
                  }[event.action] || event.action}
                </strong>{" "}
                · {new Date(event.at).toLocaleString("pt-BR")} · Responsável #
                {event.by}
                <br />
                {event.note}
              </p>
            ))}
        </details>
      )}
      {error && (
        <p role="alert" className="signature-error">
          {error}
        </p>
      )}
      {busy ? (
        <p role="status">Consultando identificação…</p>
      ) : !error && !record ? (
        <p>Nenhuma identificação registrada.</p>
      ) : (
        !error &&
        record && (
          <>
            <dl>
              <dt>Nome do registro</dt>
              <dd>{record.name}</dd>
              <dt>CPF</dt>
              <dd>{record.cpf || "—"}</dd>
              <dt>Nascimento</dt>
              <dd>{date(record.birthDate)}</dd>
            </dl>
            {record.profileCurrent === false && (
              <p className="signature-help">
                Os dados da conta foram alterados após este registro.
              </p>
            )}
            {(record.documentPortrait || record.supplementPortrait) && (
              <details className="signature-card">
                <summary>Presença de foto no documento</summary>
                <p>
                  Primeiro arquivo:{" "}
                  {record.documentPortrait?.reason || "Não conferido"}
                </p>
                {record.supplementPortrait && (
                  <p>Complementar: {record.supplementPortrait.reason}</p>
                )}
                <p className="signature-help">
                  Verifica somente presença da foto. Não há comparação facial ou
                  porcentagem de identidade.
                </p>
              </details>
            )}
            {record.analysis && (
              <details className="signature-card">
                <summary>Conferência da Nash IA</summary>
                <div className="signature-analysis-table">
                  <table>
                    <thead>
                      <tr>
                        <th>Campo</th>
                        <th>Cadastro do registro</th>
                        <th>Documento</th>
                        <th>Resultado</th>
                      </tr>
                    </thead>
                    <tbody>
                      {[
                        ["Nome", "name"],
                        ["CPF", "cpf"],
                        ["Nascimento", "birthDate"],
                      ].map(([label, key]) => (
                        <tr key={key}>
                          <td>{label}</td>
                          <td>
                            {key === "birthDate"
                              ? date(record[key])
                              : record[key] || "—"}
                          </td>
                          <td>
                            {key === "birthDate"
                              ? date(record.analysis.extracted?.[key])
                              : record.analysis.extracted?.[key] || "Não lido"}
                          </td>
                          <td>
                            {checks[record.analysis.checks?.[key]] ||
                              "Pendente"}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <p className="signature-help">{record.analysis.reason}</p>
              </details>
            )}
            {record.imagesAvailable ? (
              <EnrollmentRecord record={record} administrative />
            ) : (
              <p className="signature-help">
                {record.status === "DRAFT"
                  ? "Identificação ainda não enviada."
                  : "Imagens indisponíveis ou fora do prazo de retenção."}
              </p>
            )}
            {record.selfieQuality && (
              <p className="signature-help">
                Qualidade da foto: {record.selfieQuality.reason || "Em análise"}
                . Essa análise não é reconhecimento facial.
              </p>
            )}
            {record.acceptedAt && (
              <p className="signature-help">
                Aceite registrado em{" "}
                {new Date(record.acceptedAt).toLocaleString("pt-BR")}.
              </p>
            )}
          </>
        )
      )}
    </section>
  );
}
